// pages/Purchases.jsx

import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    AppBar,
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    LinearProgress,
    Paper,
    Toolbar,
    Typography,
} from "@mui/material";

import CloseIcon from
    "@mui/icons-material/Close";

import ExpandMoreIcon from
    "@mui/icons-material/ExpandMore";

import ShoppingCartIcon from
    "@mui/icons-material/ShoppingCart";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import {
    handleResponse,
} from "../util/integration";

import {
    POLLING_SPEED,
    usePolling,
} from "../util/polling";

import {
    fetchPurchasedLeadIdsAPI,
} from "../api/lead_api";

import Lead from
    "../entity/Lead";

import LeadPreview from
    "../fragments/LeadPreview";


const LABEL = Object.freeze({
    title:
        "Meine Käufe",

    loadMore:
        "Weitere laden",

    purchaseDate:
        "Kaufdatum",

    openLead:
        "Lead öffnen",

    closeDialog:
        "Dialog schließen",

    leadTitle: (
        leadId,
    ) => (
        `Lead #${leadId}`
    ),
});


const MESSAGE = Object.freeze({
    noPurchases:
        "Keine Ergebnisse",

    purchaseUnavailable:
        "Der Kauf konnte nicht geladen werden.",
});


const CONFIG = Object.freeze({
    initialVisibleCount:
        12,

    loadMoreCount:
        12,

    topSpacing:
        "50px",

    headerPadding:
        "25px",

    pageSpacing:
        5,

    contentSpacing:
        7,

    fullWidthColumns:
        12,

    mediumColumns:
        6,

    largeColumns:
        4,

    dialogZIndex:
        6000,
});


const STYLE = Object.freeze({
    topSpacing: {
        height:
            CONFIG.topSpacing,
    },

    titleContainer: {
        display:
            "flex",

        alignItems:
            "center",

        gap:
            1.5,
    },

    titleIcon: {
        color:
            "white",

        fontSize:
            30,
    },

    loadMoreContainer: {
        display:
            "flex",

        justifyContent:
            "center",
    },

    dialog: {
        zIndex:
            CONFIG.dialogZIndex,
    },

    dialogTitle: {
        ml:
            2,

        flex:
            1,
    },

    dialogContent: {
        backgroundColor:
            "background.default",

        margin:
            "75px",
    },
});


const COMPONENT_PROPS = Object.freeze({
    pageGrid: {
        container:
            true,

        spacing:
            CONFIG.pageSpacing,
    },

    contentGrid: {
        container:
            true,

        spacing:
            CONFIG.contentSpacing,
    },

    fullWidthGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,
    },

    purchaseGridItem: {
        item:
            true,

        xs:
            CONFIG.fullWidthColumns,

        sm:
            CONFIG.mediumColumns,

        lg:
            CONFIG.largeColumns,
    },

    title: {
        variant:
            "h6",

        component:
            "h1",
    },

    loadMoreButton: {
        variant:
            "outlined",
    },

    dialog: {
        fullScreen:
            true,
    },

    dialogAppBar: {
        position:
            "relative",
    },

    closeDialogButton: {
        edge:
            "start",

        color:
            "inherit",

        "aria-label":
            LABEL.closeDialog,
    },

    dialogTitle: {
        variant:
            "h6",

        component:
            "div",
    },

    lead: {
        seamless:
            true,
    },
});


const createColorStyles = (
    primaryColor,
) => Object.freeze({
    header: {
        p:
            CONFIG.headerPadding,

        backgroundColor:
            primaryColor,

        color:
            "white",

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "space-between",

        gap:
            2,
    },

    loadMoreButton: {
        color:
            primaryColor,

        borderColor:
            primaryColor,

        "&:hover": {
            borderColor:
                primaryColor,

            backgroundColor:
                `${primaryColor}0A`,
        },
    },

    dialogAppBar: {
        backgroundColor:
            primaryColor,
    },
});


const COLOR_STYLE =
    createColorStyles(
        PRIMARY_COLOR,
    );


const LOAD_MORE_ICON = (
    <ExpandMoreIcon />
);


const getLeadId = (
    purchase,
) => {
    if (
        purchase !== null
        && typeof purchase === "object"
    ) {
        return (
            purchase.id
            ?? purchase.leadId
            ?? null
        );
    }

    return purchase;
};


const normalizePurchaseIds = (
    response,
) => {
    if (!Array.isArray(response)) {
        return [];
    }

    const normalizedIds =
        [];

    const knownIds =
        new Set();

    response.forEach(
        (purchase) => {
            const leadId =
                getLeadId(
                    purchase,
                );

            if (
                leadId === null
                || leadId === undefined
            ) {
                return;
            }

            const leadKey =
                String(
                    leadId,
                );

            if (
                knownIds.has(
                    leadKey,
                )
            ) {
                return;
            }

            knownIds.add(
                leadKey,
            );

            normalizedIds.push(
                leadId,
            );
        },
    );

    return normalizedIds;
};


const purchaseIdsAreEqual = (
    currentIds,
    nextIds,
) => {
    if (
        currentIds.length
        !== nextIds.length
    ) {
        return false;
    }

    return currentIds.every(
        (
            currentId,
            index,
        ) => {
            return (
                String(
                    currentId,
                )
                === String(
                    nextIds[index],
                )
            );
        },
    );
};


const getPurchaseDate = (
    lead,
) => {
    return (
        lead?.recentOffer
            ?.purchaseTimestamp

        ?? lead?.purchaseTimestamp

        ?? lead?.saleTimestamp

        ?? lead?.soldTimestamp

        ?? lead?.purchaseDate

        ?? lead?.recentAuction
            ?.terminationTimestamp

        ?? lead?.recentAuction
            ?.endTimestamp

        ?? lead?.creationTimestamp
    );
};


const fetchPurchasedLeadIds = ({
    accessToken,
    userId,
}) => {
    return new Promise(
        (
            resolve,
            reject,
        ) => {
            let request;

            try {
                request =
                    fetchPurchasedLeadIdsAPI(
                        accessToken,
                        userId,
                    );
            } catch (error) {
                reject(error);
                return;
            }

            try {
                handleResponse(
                    request,

                    (response) => {
                        resolve({
                            successful:
                                true,

                            response,
                        });
                    },

                    (message) => {
                        resolve({
                            successful:
                                false,

                            message,
                        });
                    },
                );
            } catch (error) {
                reject(error);
            }
        },
    );
};


const usePurchasePolling = ({
    accessToken,
    userId,
    showMessage,
}) => {
    const [
        purchaseIds,
        setPurchaseIds,
    ] = useState([]);

    const [
        isInitialLoading,
        setIsInitialLoading,
    ] = useState(true);


    const hasUserId = (
        userId !== null
        && userId !== undefined
    );


    useEffect(() => {
        setPurchaseIds([]);

        setIsInitialLoading(
            hasUserId,
        );
    }, [
        hasUserId,
        userId,
    ]);


    const pollingRestartKey =
        useMemo(
            () => ({
                accessToken,
                userId,
            }),
            [
                accessToken,
                userId,
            ],
        );


    const pollPurchases =
        useCallback(
            async ({
                isPollingActive,
            }) => {
                if (!hasUserId) {
                    return;
                }

                const result =
                    await fetchPurchasedLeadIds({
                        accessToken,
                        userId,
                    });

                if (!isPollingActive()) {
                    return;
                }

                if (!result.successful) {
                    setIsInitialLoading(
                        false,
                    );

                    showMessage(
                        result.message,
                    );

                    return;
                }

                const normalizedIds =
                    normalizePurchaseIds(
                        result.response,
                    );

                setPurchaseIds(
                    (currentIds) => {
                        if (
                            purchaseIdsAreEqual(
                                currentIds,
                                normalizedIds,
                            )
                        ) {
                            return currentIds;
                        }

                        return normalizedIds;
                    },
                );

                setIsInitialLoading(
                    false,
                );
            },
            [
                accessToken,
                hasUserId,
                showMessage,
                userId,
            ],
        );


    const handlePollingError =
        useCallback(
            () => {
                setIsInitialLoading(
                    false,
                );
            },
            [],
        );


    usePolling({
        poll:
            pollPurchases,

        enabled:
            hasUserId,

        immediate:
            true,

        speed:
            POLLING_SPEED.medium,

        onError:
            handlePollingError,

        restartKey:
            pollingRestartKey,
    });


    return {
        purchaseIds,

        isInitialLoading,

        hasPurchases:
            purchaseIds.length > 0,
    };
};


const usePurchasePagination = ({
    purchaseIds,
    userId,
}) => {
    const [
        visibleCount,
        setVisibleCount,
    ] = useState(
        CONFIG.initialVisibleCount,
    );


    useEffect(() => {
        setVisibleCount(
            CONFIG.initialVisibleCount,
        );
    }, [
        userId,
    ]);


    const visiblePurchaseIds =
        useMemo(
            () => {
                return purchaseIds.slice(
                    0,
                    visibleCount,
                );
            },
            [
                purchaseIds,
                visibleCount,
            ],
        );


    const canLoadMore = (
        visibleCount
        < purchaseIds.length
    );


    const loadMore =
        useCallback(
            () => {
                setVisibleCount(
                    (
                        currentVisibleCount,
                    ) => {
                        return (
                            currentVisibleCount
                            + CONFIG.loadMoreCount
                        );
                    },
                );
            },
            [],
        );


    return {
        canLoadMore,
        loadMore,
        visiblePurchaseIds,
    };
};


const useLeadDialog = ({
    userId,
}) => {
    const [
        selectedLeadId,
        setSelectedLeadId,
    ] = useState(null);


    useEffect(() => {
        setSelectedLeadId(
            null,
        );
    }, [
        userId,
    ]);


    const openLeadDialog =
        useCallback(
            (
                leadId,
            ) => {
                setSelectedLeadId(
                    leadId,
                );
            },
            [],
        );


    const closeLeadDialog =
        useCallback(
            () => {
                setSelectedLeadId(
                    null,
                );
            },
            [],
        );


    const hasSelectedLead =
        selectedLeadId !== null;


    return {
        closeLeadDialog,
        hasSelectedLead,
        openLeadDialog,
        selectedLeadId,

        selectedLeadTitle:
            hasSelectedLead
                ? LABEL.leadTitle(
                    selectedLeadId,
                )
                : null,
    };
};


const usePurchases = ({
    accessToken,
    userId,
    showMessage,
}) => {
    const {
        hasPurchases,
        isInitialLoading,
        purchaseIds,
    } = usePurchasePolling({
        accessToken,
        userId,
        showMessage,
    });


    const {
        canLoadMore,
        loadMore,
        visiblePurchaseIds,
    } = usePurchasePagination({
        purchaseIds,
        userId,
    });


    const {
        closeLeadDialog,
        hasSelectedLead,
        openLeadDialog,
        selectedLeadId,
        selectedLeadTitle,
    } = useLeadDialog({
        userId,
    });


    return {
        canLoadMore,
        closeLeadDialog,
        hasPurchases,
        hasSelectedLead,
        isInitialLoading,
        loadMore,
        openLeadDialog,
        selectedLeadId,
        selectedLeadTitle,
        visiblePurchaseIds,
    };
};


function PurchasePreview({
    appAPI,
    contextAPI,
    leadId,
    openLeadDialog,
}) {
    const handleOpenLead =
        useCallback(
            () => {
                openLeadDialog(
                    leadId,
                );
            },
            [
                leadId,
                openLeadDialog,
            ],
        );


    return (
        <LeadPreview
            leadId={leadId}
            appAPI={appAPI}
            contextAPI={contextAPI}
            dateLabel={LABEL.purchaseDate}
            getDateValue={getPurchaseDate}
            actionLabel={LABEL.openLead}
            errorMessage={
                MESSAGE.purchaseUnavailable
            }
            onAction={handleOpenLead}
        />
    );
}


function PurchaseLeadDialog({
    appAPI,
    closeLeadDialog,
    contextAPI,
    hasSelectedLead,
    selectedLeadId,
    selectedLeadTitle,
}) {
    return (
        <Dialog
            {...COMPONENT_PROPS.dialog}
            open={hasSelectedLead}
            onClose={closeLeadDialog}
            sx={STYLE.dialog}
        >
            <AppBar
                {...COMPONENT_PROPS.dialogAppBar}
                sx={COLOR_STYLE.dialogAppBar}
            >
                <Toolbar>
                    <IconButton
                        {...COMPONENT_PROPS.closeDialogButton}
                        onClick={closeLeadDialog}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography
                        {...COMPONENT_PROPS.dialogTitle}
                        sx={STYLE.dialogTitle}
                    >
                        {selectedLeadTitle}
                    </Typography>
                </Toolbar>
            </AppBar>

            <DialogContent
                sx={STYLE.dialogContent}
            >
                {hasSelectedLead && (
                    <Lead
                        {...COMPONENT_PROPS.lead}
                        key={
                            String(
                                selectedLeadId,
                            )
                        }
                        appAPI={appAPI}
                        contextAPI={contextAPI}
                        leadId={selectedLeadId}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}


export default function Purchases({
    appAPI,
    contextAPI,
}) {
    const accessToken =
        contextAPI.password;

    const userId =
        contextAPI.id;

    const showMessage =
        appAPI.showMessage;


    const {
        canLoadMore,
        closeLeadDialog,
        hasPurchases,
        hasSelectedLead,
        isInitialLoading,
        loadMore,
        openLeadDialog,
        selectedLeadId,
        selectedLeadTitle,
        visiblePurchaseIds,
    } = usePurchases({
        accessToken,
        userId,
        showMessage,
    });


    return (
        <>
            <Grid
                {...COMPONENT_PROPS.pageGrid}
            >
                <Grid
                    {...COMPONENT_PROPS.fullWidthGridItem}
                >
                    <Box
                        sx={STYLE.topSpacing}
                    />
                </Grid>

                <Grid
                    {...COMPONENT_PROPS.fullWidthGridItem}
                >
                    <Grid
                        {...COMPONENT_PROPS.contentGrid}
                    >
                        <Grid
                            {...COMPONENT_PROPS.fullWidthGridItem}
                        >
                            <Paper
                                sx={COLOR_STYLE.header}
                            >
                                <Box
                                    sx={STYLE.titleContainer}
                                >
                                    <ShoppingCartIcon
                                        sx={STYLE.titleIcon}
                                    />

                                    <Typography
                                        {...COMPONENT_PROPS.title}
                                    >
                                        {LABEL.title}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>

                        {isInitialLoading && (
                            <Grid
                                {...COMPONENT_PROPS.fullWidthGridItem}
                            >
                                <LinearProgress />
                            </Grid>
                        )}

                        {!isInitialLoading
                            && !hasPurchases
                            && (
                                <Grid
                                    {...COMPONENT_PROPS.fullWidthGridItem}
                                >
                                    <Typography>
                                        {MESSAGE.noPurchases}
                                    </Typography>
                                </Grid>
                            )}

                        {!isInitialLoading
                            && visiblePurchaseIds.map(
                                (
                                    leadId,
                                ) => (
                                    <Grid
                                        {...COMPONENT_PROPS.purchaseGridItem}
                                        key={
                                            String(
                                                leadId,
                                            )
                                        }
                                    >
                                        <PurchasePreview
                                            appAPI={appAPI}
                                            contextAPI={contextAPI}
                                            leadId={leadId}
                                            openLeadDialog={
                                                openLeadDialog
                                            }
                                        />
                                    </Grid>
                                ),
                            )}

                        {!isInitialLoading
                            && canLoadMore
                            && (
                                <Grid
                                    {...COMPONENT_PROPS.fullWidthGridItem}
                                >
                                    <Box
                                        sx={STYLE.loadMoreContainer}
                                    >
                                        <Button
                                            {...COMPONENT_PROPS.loadMoreButton}
                                            startIcon={LOAD_MORE_ICON}
                                            onClick={loadMore}
                                            sx={
                                                COLOR_STYLE
                                                    .loadMoreButton
                                            }
                                        >
                                            {LABEL.loadMore}
                                        </Button>
                                    </Box>
                                </Grid>
                            )}
                    </Grid>
                </Grid>
            </Grid>

            <PurchaseLeadDialog
                appAPI={appAPI}
                closeLeadDialog={closeLeadDialog}
                contextAPI={contextAPI}
                hasSelectedLead={hasSelectedLead}
                selectedLeadId={selectedLeadId}
                selectedLeadTitle={
                    selectedLeadTitle
                }
            />
        </>
    );
}